import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';

export interface BuildInfo {
  version: string;
  environment: string;
  releaseTag: string;
  commit: string;
  runId: string;
  deployedAt: string;
}

interface RuntimeBuildInfo {
  visible?: boolean;
  version?: string;
  environment?: string;
  releaseTag?: string;
  commit?: string;
  runId?: string;
  deployedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class BuildInfoService {
  private readonly http = inject(HttpClient);
  private readonly buildInfo$ = this.http
    .get<RuntimeBuildInfo>('/version.json', {
      params: {
        cacheBust: String(Date.now()),
      },
    })
    .pipe(
      map((metadata) => normalizeBuildInfo(metadata)),
      catchError(() => of(null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

  getBuildInfo(): Observable<BuildInfo | null> {
    return this.buildInfo$;
  }
}

function normalizeBuildInfo(metadata: RuntimeBuildInfo): BuildInfo | null {
  if (metadata.visible !== true) {
    return null;
  }

  const version = readString(metadata.version);
  const releaseTag = readString(metadata.releaseTag);
  const commit = readString(metadata.commit);

  if (!version || !releaseTag || !commit) {
    return null;
  }

  return {
    version,
    environment: readString(metadata.environment) || 'unknown',
    releaseTag,
    commit,
    runId: readString(metadata.runId) || 'unknown',
    deployedAt: readString(metadata.deployedAt) || 'unknown',
  };
}

function readString(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}
